import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { networkDevicesAPI } from '../services/api';
import { NetworkDevice, NetworkDeviceStatus, NetworkDeviceType } from '../types';

const typeOptions: { label: string; value: NetworkDeviceType }[] = [
  { label: 'Router', value: 'router' },
  { label: 'Switch', value: 'switch' },
  { label: 'Firewall', value: 'firewall' },
  { label: 'Access Point', value: 'access_point' },
  { label: 'Modem', value: 'modem' },
  { label: 'Server', value: 'server' },
  { label: 'Ostalo', value: 'other' }
];

const statusOptions: { label: string; value: NetworkDeviceStatus }[] = [
  { label: 'Aktivan', value: 'active' },
  { label: 'Neaktivan', value: 'inactive' },
  { label: 'Održavanje', value: 'maintenance' },
  { label: 'Otpisan', value: 'retired' }
];

const statusSeverity: Record<NetworkDeviceStatus, 'success' | 'secondary' | 'warning' | 'danger'> = {
  active: 'success',
  inactive: 'secondary',
  maintenance: 'warning',
  retired: 'danger'
};

const ITDevices: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [devices, setDevices] = useState<NetworkDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [nameFilter, setNameFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');

  const [editVisible, setEditVisible] = useState(false);
  const [editing, setEditing] = useState<NetworkDevice | null>(null);
  const [form, setForm] = useState({
    name: '',
    type: 'switch' as NetworkDeviceType,
    deviceModel: '',
    serialNumber: '',
    location: '',
    status: 'active' as NetworkDeviceStatus,
    ipAddress: '',
    macAddress: '',
    description: ''
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await networkDevicesAPI.getAll();
      setDevices(data);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju mrežnih uređaja');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: '',
      type: 'switch',
      deviceModel: '',
      serialNumber: '',
      location: '',
      status: 'active',
      ipAddress: '',
      macAddress: '',
      description: ''
    });
    setEditVisible(true);
  };

  const openEdit = (row: NetworkDevice) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      type: row.type || 'switch',
      deviceModel: row.deviceModel || '',
      serialNumber: row.serialNumber || '',
      location: row.location || '',
      status: row.status || 'active',
      ipAddress: row.ipAddress || '',
      macAddress: row.macAddress || '',
      description: row.description || ''
    });
    setEditVisible(true);
  };

  const confirmDelete = (row: NetworkDevice) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati uređaj "${row.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await networkDevicesAPI.delete(row._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Uređaj obrisan', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju uređaja',
            life: 5000
          });
        }
      }
    });
  };

  const canSubmit = useMemo(() => {
    return (
      form.name.trim() &&
      form.deviceModel.trim() &&
      form.serialNumber.trim() &&
      form.location.trim()
    );
  }, [form]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validacija',
        detail: 'Popunite sva obavezna polja označena zvezdicom',
        life: 3000
      });
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        await networkDevicesAPI.update(editing._id, {
          ...form
        });
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Uređaj ažuriran', life: 2500 });
      } else {
        await networkDevicesAPI.create(form);
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Uređaj dodat', life: 2500 });
      }
      setEditVisible(false);
      await load();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri čuvanju uređaja',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const statusBody = (row: NetworkDevice) => (
    <Tag
      value={statusOptions.find((s) => s.value === row.status)?.label || row.status}
      severity={statusSeverity[row.status]}
    />
  );

  const portsBody = (row: NetworkDevice) => {
    const count = row.ports?.length ?? 0;
    return <span>{count > 0 ? `${count} portova` : '-'}</span>;
  };

  const actionsBody = (row: NetworkDevice) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        className="p-button-sm p-button-rounded p-button-text"
        onClick={() => openEdit(row)}
        tooltip="Uredi"
      />
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-rounded p-button-text p-button-danger"
        onClick={() => confirmDelete(row)}
        tooltip="Obriši"
      />
    </div>
  );

  const filteredDevices = devices.filter((d) => {
    const nameMatch = !nameFilter || d.name.toLowerCase().includes(nameFilter.toLowerCase());
    const typeMatch = !typeFilter || d.type.toLowerCase().includes(typeFilter.toLowerCase());
    const modelMatch = !modelFilter || d.deviceModel.toLowerCase().includes(modelFilter.toLowerCase());
    const locationMatch = !locationFilter || d.location.toLowerCase().includes(locationFilter.toLowerCase());
    const ipMatch = !ipFilter || (d.ipAddress || '').toLowerCase().includes(ipFilter.toLowerCase());
    return nameMatch && typeMatch && modelMatch && locationMatch && ipMatch;
  });

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <Card>
        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h2 className="m-0">Mrežni uređaji</h2>
          <Button label="Dodaj uređaj" icon="pi pi-plus" onClick={openNew} />
        </div>

        {error && <Message severity="error" text={error} className="mb-3" />}

        <div className="grid mb-3">
          <div className="col-12 md:col-3">
            <InputText
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Naziv"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-2">
            <InputText
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              placeholder="Tip"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-2">
            <InputText
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value)}
              placeholder="Model"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-3">
            <InputText
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              placeholder="Lokacija"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-2">
            <InputText
              value={ipFilter}
              onChange={(e) => setIpFilter(e.target.value)}
              placeholder="IP adresa"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <DataTable
          value={filteredDevices}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="Nema mrežnih uređaja"
          className="p-datatable-sm"
        >
          <Column field="name" header="Naziv" sortable />
          <Column field="type" header="Tip" sortable />
          <Column field="deviceModel" header="Model" sortable />
          <Column field="location" header="Lokacija" sortable />
          <Column field="ipAddress" header="IP adresa" sortable />
          <Column field="status" header="Status" body={statusBody} sortable />
          <Column header="Portovi" body={portsBody} />
          <Column header="Akcije" body={actionsBody} style={{ width: '160px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Uredi uređaj' : 'Dodaj uređaj'}
        visible={editVisible}
        onHide={() => setEditVisible(false)}
        style={{ width: '800px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        modal
        draggable={false}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Otkaži"
              className="p-button-secondary p-button-outlined"
              onClick={() => setEditVisible(false)}
            />
            <Button
              label={editing ? 'Sačuvaj' : 'Sačuvaj'}
              icon="pi pi-check"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={loading}
            />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label>Naziv *</label>
            <InputText
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Tip *</label>
            <Dropdown
              value={form.type}
              options={typeOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setForm((p) => ({ ...p, type: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Model *</label>
            <InputText
              value={form.deviceModel}
              onChange={(e) => setForm((p) => ({ ...p, deviceModel: e.target.value }))}
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Serijski broj *</label>
            <InputText
              value={form.serialNumber}
              onChange={(e) => setForm((p) => ({ ...p, serialNumber: e.target.value }))}
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Lokacija *</label>
            <InputText
              value={form.location}
              onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Status *</label>
            <Dropdown
              value={form.status}
              options={statusOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setForm((p) => ({ ...p, status: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>IP adresa</label>
            <InputText
              value={form.ipAddress}
              onChange={(e) => setForm((p) => ({ ...p, ipAddress: e.target.value }))}
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>MAC adresa</label>
            <InputText
              value={form.macAddress}
              onChange={(e) => setForm((p) => ({ ...p, macAddress: e.target.value }))}
            />
          </div>
          <div className="field col-12">
            <label>Opis</label>
            <InputTextarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={3}
              autoResize
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ITDevices;






